import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Phone, Mail } from "lucide-react";
import { Brand } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/book-online", label: "Book Online" },
  { to: "/membership", label: "Membership" },
  { to: "/about-us", label: "About Us" },
  { to: "/waiting-list", label: "Waiting List" },
  { to: "/contact", label: "Contact Us" },
  { to: "/terms", label: "Terms & Conditions" },
];

export function PublicLayout({ children }) {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const portalPath = user && user.role === "admin" ? "/portal/admin" : "/portal/members";

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-border">
        <div className="max-w-6xl mx-auto px-4 pt-4 pb-3">
          {/* Row 1: centered brand + mobile toggle */}
          <div className="relative flex items-center justify-center">
            <Brand size="sm" />
            <button
              data-testid="mobile-menu-toggle"
              className="md:hidden absolute right-0 p-2 text-primary"
              onClick={() => setOpen((v) => !v)}
              aria-label="Menu"
            >
              {open ? <X /> : <Menu />}
            </button>
          </div>

          {/* Row 2: nav centered below the logo */}
          <nav className="hidden md:flex items-center justify-center gap-6 mt-3">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `font-display text-lg transition-colors hover:text-primary ${
                    isActive ? "text-primary" : "text-slate-600"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Button
              data-testid="header-login-btn"
              onClick={() => navigate(user ? portalPath : "/auth")}
              className="rounded-full font-display text-lg px-7 py-5 bg-primary hover:bg-primary/90"
            >
              {user ? "My Portal" : "Login"}
            </Button>
          </nav>
        </div>

        {open && (
          <nav className="md:hidden bg-white border-t border-border px-4 py-3 flex flex-col gap-2">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                data-testid={`mnav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `font-display text-lg py-1 ${isActive ? "text-primary" : "text-slate-700"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Button
              onClick={() => { setOpen(false); navigate(user ? portalPath : "/auth"); }}
              className="rounded-full font-display text-lg py-6 bg-primary hover:bg-primary/90 mt-2"
              data-testid="mobile-login-btn"
            >
              {user ? "My Portal" : "Login"}
            </Button>
          </nav>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="bg-primary text-white mt-0">
        <div className="max-w-6xl mx-auto px-4 py-12 grid md:grid-cols-3 gap-8">
          <div>
            <div className="brand-logo-inverse text-4xl">FUN-NASTIX</div>
            <p className="font-display text-white/90 text-sm mt-1">
              Keeping Fit, Having Fun, Gymnastics is for Everyone!
            </p>
            <p className="mt-4 text-sm text-white/80">
              Term-time after-school gymnastics classes across Southampton, led by Level 4 coach Vickie Moody.
            </p>
          </div>
          <div>
            <h4 className="font-display text-xl mb-3">Quick Links</h4>
            <ul className="space-y-2 text-sm">
              {NAV.map((n) => (
                <li key={n.to}>
                  <Link to={n.to} className="text-white/85 hover:text-white transition-colors">{n.label}</Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="font-display text-xl mb-3">Contact Us</h4>
            <a href="tel:07977440009" className="flex items-center gap-2 text-sm text-white/85 hover:text-white mb-2">
              <Phone size={16} /> 07977 440 009
            </a>
            <a href="mailto:admin@fun-nastix.co.uk" className="flex items-center gap-2 text-sm text-white/85 hover:text-white">
              <Mail size={16} /> admin@fun-nastix.co.uk
            </a>
          </div>
        </div>
        <div className="border-t border-white/20 py-4 text-center text-xs text-white/70">
          © {new Date().getFullYear()} Fun-Nastix. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
